<template>
  <section class="blog-page">
    <div v-if="error" class="blog-error">{{ error }}</div>
    <div v-else-if="loading" class="blog-loading">Loading…</div>
    <template v-else-if="topic">
      <header class="blog-page-header topic-header">
        <router-link class="back-link" to="/">← Home</router-link>
        <h2>{{ topic.title }}</h2>
        <p v-if="topic.description" class="blurb">{{ topic.description }}</p>
      </header>

      <div v-if="!topic.posts?.length" class="topic-empty">
        No published posts yet.
      </div>

      <div v-else class="topic-read dflex-col">
        <div class="topic-main">
          <div v-if="postLoading" class="blog-loading">Loading post…</div>
          <div v-else-if="postError" class="blog-error">{{ postError }}</div>
          <BlogPostArticle
            v-else-if="post"
            :post="post"
            heading="h3"
            @updated="syncRail"
          />
        </div>

        <aside class="topic-rail" aria-label="Posts in this topic">
          <h3>Posts</h3>
          <ul>
            <li v-for="item in topic.posts" :key="item.slug">
              <button
                type="button"
                :class="{ 'is-current': item.slug === post?.slug }"
                :aria-current="item.slug === post?.slug ? 'page' : undefined"
                @click="openPost(item.slug)"
              >
                <span class="rail-title">{{ item.title }}</span>
                <span class="rail-meta">
                  {{ formatDate(item.created_at) }}
                  · {{ commentLabel(item.comments_count) }}
                </span>
              </button>
            </li>
          </ul>
        </aside>
      </div>
    </template>
  </section>
</template>

<script setup>
import { onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { BlogService } from "@/services/blog_service";
import BlogPostArticle from "@/components/blog/blog-post-article.vue";

const route = useRoute();
const router = useRouter();
const blog = new BlogService();
const topic = ref(null);
const post = ref(null);
const loading = ref(true);
const postLoading = ref(false);
const error = ref("");
const postError = ref("");
let postToken = 0;

function requestedSlug() {
  const value = route.query.post;
  return typeof value === "string" ? value : "";
}

function currentSlug() {
  const posts = topic.value?.posts || [];
  const requested = requestedSlug();
  if (requested && posts.some((item) => item.slug === requested)) return requested;
  return posts[0]?.slug || "";
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
}

function commentLabel(count) {
  const n = count || 0;
  return n === 1 ? "1 comment" : `${n} comments`;
}

async function loadPost(slug) {
  const token = ++postToken;
  postLoading.value = true;
  postError.value = "";
  try {
    const pkg = await blog.getPost(slug);
    if (token !== postToken) return;
    post.value = pkg.post;
  } catch (e) {
    if (token !== postToken) return;
    post.value = null;
    postError.value = "Could not load that post.";
    console.error(e);
  } finally {
    if (token === postToken) postLoading.value = false;
  }
}

async function loadTopic() {
  loading.value = true;
  error.value = "";
  postError.value = "";
  try {
    const pkg = await blog.getTopic(route.params.slug);
    topic.value = pkg.topic;
    const requested = requestedSlug();
    const posts = topic.value?.posts || [];
    if (requested && !posts.some((item) => item.slug === requested)) {
      await router.replace({
        name: "topic",
        params: { slug: route.params.slug },
        query: {}
      });
    }
    const slug = currentSlug();
    if (slug) {
      await loadPost(slug);
    } else {
      post.value = null;
      postToken += 1;
    }
  } catch (e) {
    topic.value = null;
    post.value = null;
    error.value = "Topic not found.";
    console.error(e);
  } finally {
    loading.value = false;
  }
}

function openPost(slug) {
  if (!slug || slug === post.value?.slug) return;
  const latest = topic.value?.posts?.[0]?.slug;
  router.replace({
    name: "topic",
    params: { slug: route.params.slug },
    query: slug === latest ? {} : { post: slug }
  });
}

function syncRail() {
  const row = topic.value?.posts?.find((item) => item.id === post.value?.id);
  if (!row || !post.value) return;
  row.likes_count = post.value.likes_count;
  row.comments_count = post.value.comments?.length || 0;
}

onMounted(loadTopic);

watch(() => route.params.slug, (slug, previous) => {
  if (slug && slug !== previous) loadTopic();
});

watch(() => route.query.post, () => {
  if (loading.value || !topic.value) return;
  const requested = requestedSlug();
  const posts = topic.value.posts || [];
  if (requested && !posts.some((item) => item.slug === requested)) {
    router.replace({
      name: "topic",
      params: { slug: route.params.slug },
      query: {}
    });
    return;
  }
  const slug = currentSlug();
  if (!slug || slug === post.value?.slug) return;
  loadPost(slug);
});
</script>

<style lang="scss" scoped>
@use "@/assets/styles/breakpoints" as bp;
@import "@/assets/styles/blog";

.topic-header {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(105, 158, 191, 0.35);

  h2 {
    font-size: 2rem;
    line-height: 1.2;
  }

  .blurb {
    max-width: 42rem;
    margin: 0.35rem 0 0;
    font-size: 1.05rem;
    line-height: 1.45;
  }
}

.topic-empty {
  padding: 1.25rem 1.35rem;
  border: 1px solid rgba(105, 158, 191, 0.35);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.22);
}

.topic-read {
  gap: 1.5rem;
  align-items: stretch;

  @include bp.media(lg) {
    flex-direction: row;
    align-items: flex-start;
  }
}

.topic-main {
  flex: 1 1 auto;
  min-width: 0;
}

.topic-rail {
  background: rgba(0, 0, 0, 0.28);
  border: 1px solid rgba(105, 158, 191, 0.45);
  border-radius: 10px;
  padding: 1rem 0.85rem 0.85rem;

  @include bp.media(lg) {
    flex: 0 0 280px;
    position: sticky;
    top: 0.75rem;
  }

  h3 {
    margin: 0 0.4rem 0.75rem;
    color: $fg2-base;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;

    @include bp.media(lg) {
      max-height: calc(100vh - 8rem);
      overflow: auto;
    }
  }

  li + li {
    margin-top: 0.25rem;
  }

  button {
    width: 100%;
    display: block;
    text-align: left;
    font: inherit;
    color: inherit;
    cursor: pointer;
    background: transparent;
    border: 0;
    border-left: 3px solid transparent;
    border-radius: 6px;
    padding: 0.65rem 0.7rem;

    @include bp.hover {
      &:hover {
        background: rgba(255, 255, 255, 0.05);
      }
    }

    &.is-current {
      background: rgba(105, 158, 191, 0.18);
      border-left-color: #699ebf;
    }
  }

  .rail-title {
    display: block;
    color: $fg-base-lt30;
    font-weight: 600;
    line-height: 1.3;
  }

  .rail-meta {
    display: block;
    margin-top: 0.25rem;
    color: $fg2-base;
    font-size: 0.8rem;
    opacity: 0.85;
  }
}
</style>
